import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Skeleton } from "antd";
import AirbagImage from "./airbagImage";
import { useHover } from "ahooks";
import styled from "styled-components";
import { IEquipIntroInfo, ISecureData } from "@Types/kindSumReport";
import { fetchKindsSafeEquip, fetchSafeEquipIntro } from "@Services/kindsum";
import { useQuery } from "react-query";
import LazyloadImage from "@Components/lazyImage";

const StyledModal: any = styled(Modal)`
  .ant-modal-content {
    border-radius: 4px !important;
    min-width: ${(props: any) => props.minWidth || "400px"};
  }
  .ant-modal-close-x {
    width: 38px;
    height: 38px;
    line-height: 38px;
  }
`;

const defaultSafeData: ISecureData = {
  nav: [],
  equip: {
    airbags: [],
    brakingAssistance: [],
    dynamicStability: [],
    lightingAssistance: [],
    safetySeats: "",
    safetyWarning: [],
    visionAssistance: [],
  },
};

interface IProps {
  id: number;
  safeEquipModelYearId: number;
}

const Secure: React.FC<IProps> = ({ safeEquipModelYearId }) => {
  const ref = useRef(null);
  const refEquip = useRef(null);
  const isHovering = useHover(ref);
  const isEquipHovering = useHover(refEquip);
  const [active, setActive] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenEquip, setIsModalOpenEquip] = useState({
    open: false,
    equipIntro: "",
  });
  const [equipIntroInfo, setEquipIntroInfo] = useState<IEquipIntroInfo>();
  const [isModalOpenEquipImg, setIsModalOpenEquipImg] = useState(false);
  const [loadingEquip, setLoadingEquip] = useState(true);
  const [isMount, setIsMount] = useState(false);

  const { data = defaultSafeData } = useQuery<ISecureData>(["fetchKindsSafeEquip", safeEquipModelYearId], async () => {
    if (!safeEquipModelYearId) return [];
    try {
      const res: { data; status } = await fetchKindsSafeEquip({ modelYearId: safeEquipModelYearId });
      setIsMount(true);
      if (res?.status === 12000) {
        return res.data;
      }
    } catch (error) {}
    return defaultSafeData;
  });
  const { nav, equip } = data;

  const getSafeEquipIntro = async () => {
    try {
      const res: { status; data } = await fetchSafeEquipIntro({
        kindId: window.query_kind_id,
        entryName: isModalOpenEquip.equipIntro,
      });
      if (res.status === 12000) {
        setEquipIntroInfo(res.data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    !!nav.length && setActive(nav[0].key);
  }, [data]);

  useEffect(() => {
    if (!!isModalOpenEquip.equipIntro) {
      setLoadingEquip(true);
      getSafeEquipIntro();
    }
  }, [isModalOpenEquip.equipIntro]);

  const navComp = useMemo(() => {
    if (!nav.length) return <Skeleton style={{ width: "260px", height: "50px" }} active={false} />;
    return nav.map((item, index) => (
      <p
        key={item.key}
        className={`${index < 4 ? "mb-14" : ""} ${
          active === item.key ? "text-[#3264FF]" : "text-[#999999]"
        } text-12 cursor-pointer text-center mr-16 w-48 min-w-[48px]`}
        onClick={() => setActive(item.key)}
      >
        {item.title}
      </p>
    ));
  }, [nav]);

  return (
    <div id='Secure' className='w-full px-12 pt-20'>
      <div className='flex flex-wrap whitespace-normal break-all' id='SecureNav'>
        {navComp}
      </div>
      {active === "airbags" && (
        <>
          <div
            className='h-[254px] flex items-center justify-center relative cursor-pointer'
            ref={ref}
            onClick={() => setIsModalOpen(!isModalOpen)}
            id='SafeAirBags'
          >
            <LazyloadImage
              Loaded={isMount}
              className={`${isHovering ? "absolute" : "none"} w-36 h-28 top-0 left-0 right-0 bottom-0 m-auto z-10`}
              src='https://photo.8891.com.tw/2024/09/10/1725931615050455.png'
              alt=''
            />

            {!isMount ? (
              <Skeleton.Image active={false} style={{ width: "192px", height: "100px" }} />
            ) : (
              <AirbagImage width={192} height={100} scale={1} data={equip[active]} />
            )}
          </div>
          {!!equip?.safetySeats && (
            <p className='text-[#999] text-12 line-clamp-2 w-full'>該車款配備：兒童安全椅固定裝置{equip.safetySeats}</p>
          )}
        </>
      )}

      {active !== "" && !!equip?.[active].length && active !== "airbags" && (
        <div className='mt-18 pb-12' key={active}>
          <div className='h-[220px]'>
            {equip[active].map((item, index) => (
              <div
                className={`flex w-258 h-44  border-solid border-[#E8E8E8] border-0 border-t-1 border-r-1 border-l-1 `}
                key={item.title + index}
                data-title={item.title}
                id='SecureEquip'
                onClick={() => {
                  setIsModalOpenEquip({
                    open: !isModalOpenEquip.open,
                    equipIntro: item.title,
                  });
                }}
              >
                <div
                  className={`text-[#333333] text-14 cursor-pointer flex items-center justify-start pl-16 flex-1 ${
                    index + 1 === equip[active].length ? "border-b-1" : ""
                  }`}
                >
                  <span className="border-0 border-b-1 border-solid border-[#333333] leading-[12px]">{item.title}</span>
                </div>
                <p
                  className={`w-44 border-0 flex justify-center items-center border-l-1 border-solid border-[#E8E8E8] ${
                    index + 1 === equip[active].length ? "border-b-1" : ""
                  }`}
                >
                  <i
                    className={`w-8 h-8 text-[#646464] flex justify-center items-center rounded-full text-[20px] ${
                      item.value === "y"
                        ? "bg-[#333333]"
                        : item.value === "n"
                        ? "border-1 border-solid border-[#333333]"
                        : ""
                    }`}
                  >
                    {item.value === "-" ? "-" : ""}
                  </i>
                </p>
              </div>
            ))}
          </div>

          <div className='flex mt-26'>
            {["標配", "選配", "無"].map((item, index) => (
              <div className='flex items-center justify-start mr-10' key={index}>
                <i
                  className={`w-8 h-8 text-[#646464] flex justify-center items-center rounded-full ${
                    index === 0 ? "bg-[#333333]" : index == 1 ? "border-1 border-solid border-[#333333]" : ""
                  }`}
                >
                  {index === 2 ? "-" : ""}
                </i>
                <p className='text-[#999999] text-12 ml-4 leading-18'>{item}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 安全气囊大图 */}
      <StyledModal
        open={isModalOpen}
        footer={null}
        maskClosable={false}
        destroyOnClose={false}
        centered={true}
        width={835}
        minWidth={"835px"}
        bodyStyle={{ padding: "40px", minWidth: "835px" }}
        onCancel={() => setIsModalOpen(!isModalOpen)}
      >
        <div className='h-[450px] flex items-center justify-center'>
          <AirbagImage width={563} height={290} scale={2.93} text={16} data={equip[active]} />
        </div>
      </StyledModal>

      {/* 辅助配备 */}
      <StyledModal
        open={isModalOpenEquip.open}
        footer={null}
        maskClosable={false}
        destroyOnClose={false}
        centered={true}
        width={1060}
        minWidth={"1060px"}
        bodyStyle={{ padding: "0", minWidth: "1060px" }}
        onCancel={() => setIsModalOpenEquip({ open: false, equipIntro: isModalOpenEquip.equipIntro })}
      >
        <div
          className='w-full h-56 flex items-center rounded-tl-4 rounded-tr-4'
          style={{ background: "linear-gradient(270deg,#f7faff 12%, #e4ebfb 92%)" }}
        >
          <img
            className='w-120 h-26 ml-32'
            src='https://photo.8891.com.tw/2024/09/10/1725947855468790.png'
            alt='8891新車'
          />
        </div>
        <div className='pt-30 pr-10 pl-30 pb-30 flex'>
          <div
            className='relative cursor-pointer'
            ref={refEquip}
            onClick={() => setIsModalOpenEquipImg(!isModalOpenEquipImg)}
          >
            {loadingEquip && <Skeleton.Image active={false} style={{ width: "498px", height: "332px" }} />}
            <img
              className={`${loadingEquip ? "hidden" : ""} w-[498px] h-[332px]`}
              src={equipIntroInfo?.image}
              alt=''
              id='EquipIntroInfoImage'
              onLoad={() => setLoadingEquip(false)}
            />
            <img
              className={`${
                isEquipHovering ? "absolute" : "none"
              }  w-36 h-28 absolute top-0 left-0 right-0 bottom-0 m-auto z-10`}
              src='https://photo.8891.com.tw/2024/09/10/1725931615050455.png'
              alt=''
            />
          </div>
          <div className='flex flex-col text-38 pr-10 ml-30'>
            <div className='flex items-end'>
              <h1 className='text-[#333333] m-0 font-semibold leading-[52px] text-[38px]'>
                {equipIntroInfo?.title || "-"}
              </h1>
              {!!equipIntroInfo?.nickTitle && (
                <p className='text-[#999999] text-16 ml-12 font-normal pb-8'>({equipIntroInfo?.nickTitle})</p>
              )}
            </div>
            {!!equipIntroInfo?.enTitle && (
              <p className='text-[#999999] text-30 leading-[42px] mb-12'>{equipIntroInfo?.enTitle}</p>
            )}
            <div className='mb-12 text-14 flex items-start justify-start'>
              <p className='text-[#333333] mr-4 w-[70px] leading-20'>工作原理：</p>
              <p className='text-[#666666] leading-20 w-[388px]'>{equipIntroInfo?.workingPrinciple || "-"}</p>
            </div>
            <div className='mb-12 text-14 flex items-start justify-start'>
              <p className='text-[#333333] mr-4 w-[70px] leading-20'>安全作用：</p>
              <p className='text-[#666666] leading-20 w-[388px]'>{equipIntroInfo?.safeEfficacy || "-"}</p>
            </div>
            {(!!equipIntroInfo?.standardModelYear || !!equipIntroInfo?.optionalModelYear) && (
              <>
                <i className='mt-18 mb-30 w-full h-1 bg-[#E8E8E8]' />
                {!!equipIntroInfo?.standardModelYear && (
                  <div className='mb-12 text-14 flex items-start justify-start'>
                    <p className='text-[#333333] mr-4 w-[70px] leading-20'>標配車型：</p>
                    <p className='text-[#666666] leading-20 w-[388px]'>{equipIntroInfo?.standardModelYear}</p>
                  </div>
                )}
                {!!equipIntroInfo?.optionalModelYear && (
                  <div className='mb-12 text-14 flex items-start justify-start'>
                    <p className='text-[#333333] mr-4 w-[70px] leading-20'>選配車型：</p>
                    <p className='text-[#666666] leading-20 w-[388px]'>{equipIntroInfo?.optionalModelYear}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </StyledModal>

      {/* 辅助配备大图 */}
      <StyledModal
        open={isModalOpenEquipImg}
        footer={null}
        maskClosable={true}
        destroyOnClose={false}
        closable={false}
        centered={true}
        width={868}
        minWidth={"868px"}
        bodyStyle={{ padding: "0", minWidth: "868px" }}
        onCancel={() => setIsModalOpenEquipImg(false)}
      >
        <img className='w-[868px] h-[578px]' src={equipIntroInfo?.image} alt='' />
      </StyledModal>
    </div>
  );
};

export default Secure;
